import { useState } from "react";
import { useAudioRecorder } from "../hooks/useAudioRecorder";
import { transcribeAudio, type TranscribeResponse } from "../lib/http/speechApi";
import { costProjections, formatCost, formatDuration, formatMs } from "../lib/format";

interface VoiceSearchBarProps {
  placeholder?: string;
  onSearch?: (query: string) => void;
  /** Surfaces provider/latency/cost/confidence after each voice transcription — for eyeballing quality. */
  onTranscribed?: (result: TranscribeResponse, clientLatencyMs: number) => void;
}

export default function VoiceSearchBar({
  placeholder = "Search deals, destinations…",
  onSearch,
  onTranscribed,
}: VoiceSearchBarProps) {
  const [query, setQuery] = useState("");
  const [lastResult, setLastResult] = useState<{ data: TranscribeResponse; clientLatencyMs: number } | null>(null);
  const [transcribing, setTranscribing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { status, errorMessage, start, stop } = useAudioRecorder();

  const isRecording = status === "recording";

  const handleMicClick = async () => {
    if (isRecording) {
      const blob = await stop();
      if (!blob) return;
      setTranscribing(true);
      setError(null);
      try {
        const result = await transcribeAudio(blob, { includeWords: true });
        setQuery(result.data.text);
        setLastResult(result);
        onTranscribed?.(result.data, result.clientLatencyMs);
      } catch (err: any) {
        setError(err?.response?.data?.detail || err?.message || "Transcription failed");
      } finally {
        setTranscribing(false);
      }
      return;
    }
    setLastResult(null);
    setError(null);
    await start();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onSearch?.(query);
  };

  return (
    <div className="w-full max-w-2xl">
      <form onSubmit={handleSubmit} className="flex items-center gap-2 rounded-full border border-gray-300 bg-white px-4 py-2 shadow-sm focus-within:ring-2 focus-within:ring-indigo-400">
        <svg className="h-5 w-5 text-gray-400 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-4.35-4.35M11 19a8 8 0 100-16 8 8 0 000 16z" />
        </svg>
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={placeholder}
          className="flex-1 border-none bg-transparent text-sm outline-none placeholder:text-gray-400"
        />
        <button
          type="button"
          onClick={handleMicClick}
          disabled={transcribing}
          aria-label={isRecording ? "Stop recording" : "Start voice search"}
          className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full transition-colors ${
            isRecording
              ? "bg-red-500 text-white animate-pulse"
              : transcribing
                ? "bg-gray-200 text-gray-400"
                : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
          }`}
        >
          {transcribing ? (
            <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          ) : (
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 14a3 3 0 003-3V6a3 3 0 10-6 0v5a3 3 0 003 3z" />
              <path d="M17 11a1 1 0 10-2 0 3 3 0 01-6 0 1 1 0 10-2 0 5 5 0 004 4.9V18H9a1 1 0 100 2h6a1 1 0 100-2h-2v-2.1a5 5 0 004-4.9z" />
            </svg>
          )}
        </button>
      </form>

      {(errorMessage || error) && (
        <p className="mt-1 text-xs text-red-600">{errorMessage || error}</p>
      )}

      {lastResult && (
        <>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">
              {lastResult.data.provider} · {lastResult.data.model}
            </span>
            <span className="rounded-full bg-blue-50 px-2 py-1 text-blue-700">
              ⏱ client {formatMs(lastResult.clientLatencyMs)} · server {formatMs(lastResult.data.latency_ms)}
            </span>
            <span className="rounded-full bg-green-50 px-2 py-1 text-green-700">
              💰 {formatCost(lastResult.data.cost_usd)} for {formatDuration(lastResult.data.duration_seconds)} audio
            </span>
            <span className="rounded-full bg-purple-50 px-2 py-1 text-purple-700">
              🎯 {lastResult.data.confidence !== null ? `${(lastResult.data.confidence * 100).toFixed(0)}%` : "confidence n/a"}
            </span>
            {lastResult.data.total_tokens !== null && (
              <span className="rounded-full bg-gray-100 px-2 py-1 text-gray-600">
                🔢 {lastResult.data.total_tokens} tokens
              </span>
            )}
            {lastResult.data.low_confidence_words.length > 0 && (
              <span className="rounded-full bg-amber-50 px-2 py-1 text-amber-700">
                ⚠ unsure: {lastResult.data.low_confidence_words.join(", ")}
              </span>
            )}
          </div>
          {(() => {
            const proj = costProjections(lastResult.data.cost_usd, lastResult.data.duration_seconds);
            if (!proj) return null;
            return (
              <p className="mt-1 text-xs text-gray-500">
                At this rate: {formatCost(proj.per5s)} / 5s · {formatCost(proj.per10s)} / 10s · {formatCost(proj.perMin)} / min
              </p>
            );
          })()}
        </>
      )}
    </div>
  );
}
